// The interactive session's rules, injected as one policy object (addendum
// A-2), so a later voice mode can reuse ./session.ts with different stop,
// commit and hidden-page rules. `DICTATION_POLICY` is today's behaviour;
// BUILD 2's `VOICE_POLICY` lives in ui/voice/policy.ts.
import type { MicKind } from '../contracts';
import { VOICE_ACTIVITY_LEVEL } from '../capture/level';

export type InteractivePolicy = {
  /** The microphone claim this mode makes. */
  micKind: MicKind;
  /**
   * Stop after the server's `idleStopSec` without speech, by the client energy
   * VAD. A pause shorter than that never stops the session.
   */
  idleStop: boolean;
  /** A countdown is shown for the last part of the idle wait. */
  idleCountdownMs: number;
  /** The limit warning appears this long before `maxSessionSec`. */
  warnBeforeMs: number;
  /**
   * What an automatic stop (idle, the length cap) does with the text: insert
   * it at the caret, or hold it for an explicit Insert. Never send.
   */
  autoStop: 'insert' | 'hold';
  /** Page hidden, device lost or audio interrupted: stop and hold the text, or keep going. */
  onInterrupt: 'stop-hold' | 'continue';
  /**
   * What commits a turn. 'explicit': only Insert, Enter or an automatic stop
   * (dictation). 'endpoint': the provider's end of turn in hands-free
   * voice mode (C2-4, C2-8).
   */
  commit: 'explicit' | 'endpoint';
  voiceActivityLevel: number;
};

export const DICTATION_POLICY: InteractivePolicy = Object.freeze({
  micKind: 'dictation',
  idleStop: true,
  idleCountdownMs: 5_000,
  warnBeforeMs: 30_000,
  autoStop: 'insert',
  onInterrupt: 'stop-hold',
  commit: 'explicit',
  voiceActivityLevel: VOICE_ACTIVITY_LEVEL,
});
